/**
 * CopyContentDialog — copy selected fields from one audience's day to another.
 *
 * Shows:
 *   1. Target audience selector (can't copy to same audience)
 *   2. Field checkboxes — with source content indicators
 *   3. Conflict strategy (fill empty / replace all)
 *   4. Live preview — what will happen to each field
 */

import { useState, useMemo } from 'react';
import { Copy, Check, X, AlertCircle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/utils/cn';
import { AUDIENCES, AUDIENCE_LABELS } from '@/types/auth.types';
import type { Audience } from '@/types/auth.types';
import type { DailyContent, ContentFieldKey, CopyConflictStrategy } from '@/types/content.types';
import { CONTENT_FIELD_KEYS, CONTENT_FIELD_LABELS } from '@/types/content.types';
import { previewCopy } from '@/services/copyService';
import Button from '@/components/common/Button';

// ─── Props ─────────────────────────────────────────────────────────────────────

interface CopyContentDialogProps {
  open:           boolean;
  sourceAudience: Audience;
  dayNumber:      number;
  sourceContent:  DailyContent | null;
  /** Pass the already-loaded target content when available (avoids extra fetch) */
  getTargetContent: (audience: Audience) => DailyContent | null | undefined;
  onCopy: (
    targetAudience:   Audience,
    fields:           ContentFieldKey[],
    conflictStrategy: CopyConflictStrategy,
  ) => void;
  onClose:  () => void;
  isCopying: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function CopyContentDialog({
  open,
  sourceAudience,
  dayNumber,
  sourceContent,
  getTargetContent,
  onCopy,
  onClose,
  isCopying,
}: CopyContentDialogProps) {
  const otherAudiences = AUDIENCES.filter(a => a !== sourceAudience);

  const [targetAudience,   setTargetAudience]   = useState<Audience>(otherAudiences[0]);
  const [selectedFields,   setSelectedFields]   = useState<ContentFieldKey[]>([...CONTENT_FIELD_KEYS]);
  const [conflictStrategy, setConflictStrategy] = useState<CopyConflictStrategy>('empty_only');
  const [showPreview,      setShowPreview]      = useState(true);

  const targetContent = getTargetContent(targetAudience) ?? null;

  // Compute preview
  const preview = useMemo(() => previewCopy(
    sourceContent,
    targetContent,
    selectedFields,
    [...CONTENT_FIELD_KEYS],
    conflictStrategy,
  ), [sourceContent, targetContent, selectedFields, conflictStrategy]);

  const willCopyCount  = preview.filter(p => p.willCopy).length;
  const willSkipCount  = preview.filter(p => !p.willCopy && p.reason !== 'not_selected').length;

  const toggleField = (field: ContentFieldKey) => {
    setSelectedFields(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const selectAll  = () => setSelectedFields([...CONTENT_FIELD_KEYS]);
  const selectNone = () => setSelectedFields([]);

  const handleCopy = () => {
    if (selectedFields.length === 0) return;
    onCopy(targetAudience, selectedFields, conflictStrategy);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden
                   flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50">
              <Copy className="h-4 w-4 text-primary-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-surface-900">Copy to another audience</h2>
              <p className="text-xs text-surface-500">Day {dayNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg
                       text-surface-400 hover:text-surface-700 hover:bg-surface-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* Source → Target */}
          <div className="flex items-center gap-3">
            <div className="flex-1 rounded-xl border border-surface-200 bg-surface-50 px-4 py-3">
              <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wide mb-1">
                From
              </p>
              <p className="text-sm font-semibold text-surface-900">
                {AUDIENCE_LABELS[sourceAudience]}
              </p>
            </div>

            <ArrowRight className="h-5 w-5 text-surface-400 shrink-0" />

            <div className="flex-1 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
              <p className="text-[10px] font-semibold text-primary-500 uppercase tracking-wide mb-1.5">
                To
              </p>
              <div className="flex flex-col gap-1.5">
                {otherAudiences.map(aud => (
                  <label key={aud} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="targetAudience"
                      value={aud}
                      checked={targetAudience === aud}
                      onChange={() => setTargetAudience(aud)}
                      className="accent-primary-600"
                    />
                    <span className="text-sm font-medium text-surface-900">
                      {AUDIENCE_LABELS[aud]}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Conflict strategy */}
          <div>
            <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">
              If target already has content
            </p>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  {
                    value: 'empty_only' as CopyConflictStrategy,
                    label: 'Fill empty only',
                    desc:  "Don't overwrite existing fields",
                  },
                  {
                    value: 'replace' as CopyConflictStrategy,
                    label: 'Replace all',
                    desc:  'Overwrite everything selected',
                  },
                ]
              ).map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setConflictStrategy(opt.value)}
                  className={cn(
                    'rounded-xl border p-3 text-left transition-all',
                    conflictStrategy === opt.value
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-surface-200 hover:border-surface-300',
                  )}
                >
                  <p className={cn(
                    'text-xs font-semibold',
                    conflictStrategy === opt.value ? 'text-primary-700' : 'text-surface-700',
                  )}>
                    {opt.label}
                  </p>
                  <p className="text-[10px] text-surface-400 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Field selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide">
                Fields to copy
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  All
                </button>
                <span className="text-surface-300">·</span>
                <button
                  type="button"
                  onClick={selectNone}
                  className="text-xs text-surface-500 hover:text-surface-700 font-medium"
                >
                  None
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-surface-200 divide-y divide-surface-100 overflow-hidden">
              {CONTENT_FIELD_KEYS.map(field => {
                const hasSource = !!(sourceContent?.[field]);
                const isSelected = selectedFields.includes(field);
                return (
                  <label
                    key={field}
                    className={cn(
                      'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
                      isSelected ? 'bg-white' : 'bg-surface-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleField(field)}
                      className="accent-primary-600 h-4 w-4 shrink-0"
                    />
                    <span className="flex-1 text-sm text-surface-800">
                      {CONTENT_FIELD_LABELS[field]}
                    </span>
                    {/* Source content indicator */}
                    <span className={cn(
                      'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                      hasSource
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-surface-100 text-surface-400',
                    )}>
                      {hasSource ? 'has content' : 'empty'}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Live preview */}
          <div>
            <button
              type="button"
              onClick={() => setShowPreview(p => !p)}
              className="flex items-center gap-2 w-full text-left mb-2"
            >
              <p className="text-xs font-semibold text-surface-500 uppercase tracking-wide flex-1">
                Preview
              </p>
              {showPreview
                ? <ChevronUp className="h-3.5 w-3.5 text-surface-400" />
                : <ChevronDown className="h-3.5 w-3.5 text-surface-400" />
              }
            </button>

            {showPreview && (
              <div className="rounded-xl border border-surface-200 divide-y divide-surface-100 overflow-hidden">
                {preview.map(p => (
                  <div key={p.field} className="flex items-center gap-3 px-4 py-2">
                    <span className="flex-1 text-xs text-surface-700">
                      {CONTENT_FIELD_LABELS[p.field]}
                    </span>

                    {p.reason === 'not_selected' && (
                      <span className="text-[10px] text-surface-400">—</span>
                    )}
                    {p.reason === 'skipped_empty_source' && (
                      <span className="text-[10px] text-surface-400">Source empty</span>
                    )}
                    {p.reason === 'skipped_conflict' && (
                      <span className="flex items-center gap-1 text-[10px] text-amber-600">
                        <AlertCircle className="h-3 w-3" /> Skipped (target has content)
                      </span>
                    )}
                    {p.willCopy && (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                        <Check className="h-3 w-3" />
                        {p.hasTarget ? 'Will overwrite' : 'Will copy'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-200 bg-surface-50
                        flex items-center justify-between gap-4">
          {/* Summary */}
          <p className="text-xs text-surface-500">
            {willCopyCount > 0
              ? <><span className="text-emerald-600 font-semibold">{willCopyCount}</span> field{willCopyCount !== 1 ? 's' : ''} will copy</>
              : 'No fields will be copied'
            }
            {willSkipCount > 0 && (
              <> · <span className="text-amber-600 font-semibold">{willSkipCount}</span> skipped</>
            )}
          </p>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} disabled={isCopying}>
              Cancel
            </Button>
            <Button
              onClick={handleCopy}
              loading={isCopying}
              disabled={willCopyCount === 0}
              leftIcon={<Copy className="h-4 w-4" />}
            >
              Copy {willCopyCount > 0 ? `${willCopyCount} field${willCopyCount !== 1 ? 's' : ''}` : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
